/**
 * Shared Tippo PDF layout tokens and helpers for pdf-lib generators
 * (tip reports, CRA allocation statements, etc.).
 * @packageDocumentation
 */
/** A4 portrait (points). */
export const TIPPO_PDF_PAGE_SIZE = [595, 842];
export const TIPPO_PDF_GRID = {
    marginX: 44,
    marginTop: 44,
    marginBottom: 44,
    footerHeight: 40,
    gutter: 12,
};
export const TIPPO_PDF_SPACE = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 18,
    xl: 26,
};
export const TIPPO_PDF_CARD_METRICS = {
    paddingX: 16,
    paddingY: 14,
    headerBandHeight: 96,
    summaryHeight: 64,
    breakdownRowHeight: 18,
};
export const TIPPO_PDF_TABLE_METRICS = {
    headerHeight: 26,
    rowHeight: 18,
    cellPadY: 7,
    strokeWidth: 0.8,
    rowRuleWidth: 0.6,
};
export const TIPPO_PDF_PALETTE = {
    mauveShadow950: "#170d12",
    mauveShadow900: "#21121a",
    mauveShadow700: "#63364f",
    mauveShadow600: "#844869",
    mauveShadow200: "#dbbdcd",
    mauveShadow100: "#eddee6",
    mauveShadow50: "#f6eff3",
    mintLeaf600: "#4b815b",
    mintLeaf50: "#eff6f1",
    blushRose600: "#98344f",
};
export const TIPPO_PDF_TYPE = {
    title: { size: 18, line: 22 },
    h2: { size: 12, line: 16 },
    h3: { size: 10.5, line: 14 },
    body: { size: 10, line: 14 },
    small: { size: 9, line: 12 },
    tiny: { size: 8, line: 10 },
};
export function formatUsd(cents) {
    return `$${(cents / 100).toFixed(2)}`;
}
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
export function hexToRgb01(hex) {
    const normalized = hex.replace("#", "").trim();
    if (normalized.length !== 6) {
        throw new Error(`Invalid hex color: ${hex}`);
    }
    const r = parseInt(normalized.slice(0, 2), 16) / 255;
    const g = parseInt(normalized.slice(2, 4), 16) / 255;
    const b = parseInt(normalized.slice(4, 6), 16) / 255;
    return { r, g, b };
}
export function ellipsizeToWidth(options) {
    const { text, maxWidth, font, size } = options;
    if (font.widthOfTextAtSize(text, size) <= maxWidth)
        return text;
    const ellipsis = "…";
    const ellipsisWidth = font.widthOfTextAtSize(ellipsis, size);
    if (ellipsisWidth >= maxWidth)
        return ellipsis;
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        const candidate = text.slice(0, mid) + ellipsis;
        if (font.widthOfTextAtSize(candidate, size) <= maxWidth)
            lo = mid;
        else
            hi = mid - 1;
    }
    if (lo <= 0)
        return ellipsis;
    return text.slice(0, lo) + ellipsis;
}
/**
 * pdf-lib draws text from the baseline; for fixed-height table rows we want
 * the glyph box to look vertically centered within the fill.
 */
export function getCenteredBaselineY(options) {
    return options.topY - options.rowHeight / 2 - options.fontSize * 0.38;
}
export function createTippoPdfTheme(rgb) {
    const rgbHex = (hex) => {
        const { r, g, b } = hexToRgb01(hex);
        return rgb(r, g, b);
    };
    const palette = TIPPO_PDF_PALETTE;
    return {
        text: rgbHex(palette.mauveShadow950),
        mutedText: rgbHex(palette.mauveShadow600),
        accent: rgbHex(palette.mauveShadow700),
        border: rgbHex(palette.mauveShadow200),
        surface: rgbHex("#ffffff"),
        surfaceMuted: rgbHex(palette.mauveShadow50),
        headerFill: rgbHex(palette.mauveShadow100),
        stripeFill: rgbHex(palette.mauveShadow50),
        success: rgbHex(palette.mintLeaf600),
        danger: rgbHex(palette.blushRose600),
    };
}
